import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Radio, Video, ShieldCheck, CreditCard, LogOut, LayoutGrid, Heart, Scissors, Search, Coins, Mic, Compass, Shield, PlayCircle, Swords, Crown, Wallet, TrendingUp, Menu, Info, DollarSign, Mail, FileText, Lock, Trash2, ArrowLeft, RotateCw, Star, Sparkles, Users, BookOpen, Bug, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BuyCoinsDialog } from "@/components/BuyCoinsDialog";
import { NotificationBell } from "@/components/NotificationBell";
import { AccentPicker } from "@/components/AccentPicker";
import { useBillingGate } from "@/lib/twa";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export const Navbar = () => {
  const { user, logout, coins } = useAuth();
  const { gated } = useBillingGate();
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const [showCoins, setShowCoins] = useState(false);
  const headerRef = useRef(null);

  // Publish the navbar's real height as --nav-h so sticky elements (Multiview, chat)
  // pin right below it regardless of whether the search row is showing.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const setH = () => document.documentElement.style.setProperty("--nav-h", `${el.offsetHeight}px`);
    setH();
    const ro = new ResizeObserver(setH);
    ro.observe(el);
    window.addEventListener("resize", setH);
    return () => { ro.disconnect(); window.removeEventListener("resize", setH); };
  }, []);

  const links = [
    { to: "/", label: "Browse", icon: Radio },
    { to: "/following", label: "Following", icon: Heart },
    { to: "/clips", label: "Clips", icon: Scissors },
    { to: "/podcasts", label: "Podcasts", icon: Mic },
    { to: "/multiview", label: "Multiview", icon: LayoutGrid },
    { to: "/pk", label: "PK Battle", icon: Swords },
    { to: "/community", label: "Community", icon: Users },
  ];
  // Only the core links sit in the top bar; the rest stay in the "More" menu so
  // the bar never overflows and the account name is always readable.
  const navLinks = links.filter((l) => !["/clips", "/podcasts"].includes(l.to));

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header ref={headerRef} className="glass sticky top-0 z-50 border-b border-white/10" data-testid="navbar">
      <div className="shell flex items-center gap-3 py-3.5">
        {loc.pathname !== "/" && (
          <button onClick={() => nav(-1)} data-testid="nav-back" title="Go back" aria-label="Go back"
            className="flex shrink-0 items-center justify-center rounded-full border border-white/10 p-2 text-zinc-300 transition-colors hover:border-[#00FFC2] hover:text-white">
            <ArrowLeft size={18} />
          </button>
        )}
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
          <img src="/airwave-icon.png" alt="Air Wave" className="h-9 w-9 object-contain" />
          <span className="hidden font-display text-xl font-extrabold tracking-tighter text-glow sm:block">AIR WAVE</span>
        </Link>

        <button onClick={() => window.location.reload()} data-testid="nav-refresh" title="Refresh page" aria-label="Refresh page"
          className="flex shrink-0 items-center justify-center rounded-full border border-white/10 p-2 text-zinc-300 transition-colors hover:border-[#00FFC2] hover:text-white active:scale-90">
          <RotateCw size={18} />
        </button>

        <nav className="hidden items-center gap-1 xl:flex">
          {navLinks.map((l) => {
            const active = loc.pathname === l.to;
            const Icon = l.icon;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
                className={`flex items-center gap-2 whitespace-nowrap rounded-full px-2.5 py-2 text-sm font-medium transition-colors ${
                  active ? "bg-[#00FFC2] text-black" : "text-zinc-400 hover:bg-white/5 hover:text-white"
                }`}
              >
                <Icon size={16} /> {l.label}
              </Link>
            );
          })}
        </nav>

        {/* All company/legal links + tour, reachable from the top — no scrolling to the footer */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="more-menu" className="flex shrink-0 items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium text-zinc-400 transition-colors hover:bg-white/5 hover:text-white">
              <Menu size={16} /> <span className="hidden sm:block">More</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-[88vh] w-56 overflow-y-auto border-white/10 bg-[#0C0C14] text-white [&_[role=menuitem]]:py-1 [&_[role=separator]]:my-0.5">
            {/* Product / explore — mirrors the footer so everything is reachable without scrolling */}
            <div>
              <div className="px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Product</div>
              {links.map((l) => {
                const Icon = l.icon;
                return (
                  <DropdownMenuItem key={l.to} onClick={() => nav(l.to)} className="cursor-pointer focus:bg-white/10">
                    <Icon size={15} className="mr-2 text-[#00FFC2]" /> {l.label}
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuSeparator className="bg-white/10" />
            </div>

            <div className="px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Company</div>
            <DropdownMenuItem onClick={() => nav("/about")} data-testid="more-about" className="cursor-pointer focus:bg-white/10">
              <Info size={15} className="mr-2 text-[#00FFC2]" /> About Us
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav("/blog")} data-testid="more-blog" className="cursor-pointer focus:bg-white/10">
              <BookOpen size={15} className="mr-2 text-[#00FFC2]" /> Guides &amp; Resources
            </DropdownMenuItem>
            {[
              { slug: "how-to-start-live-streaming-2026", label: "How to Start Streaming" },
              { slug: "obs-setup-guide-for-streamers", label: "OBS Setup Guide" },
              { slug: "best-streaming-setup-for-beginners", label: "Best Beginner Gear" },
              { slug: "how-streamers-make-money", label: "How Streamers Make Money" },
              { slug: "grow-your-streaming-audience", label: "Grow Your Audience" },
            ].map((g) => (
              <DropdownMenuItem
                key={g.slug}
                onClick={() => nav(`/blog/${g.slug}`)}
                data-testid={`more-guide-${g.slug}`}
                className="cursor-pointer pl-9 text-[13px] text-white/70 focus:bg-white/10"
              >
                {g.label}
              </DropdownMenuItem>
            ))}
            {!gated && (
              <DropdownMenuItem onClick={() => nav("/pricing")} data-testid="more-pricing" className="cursor-pointer focus:bg-white/10">
                <DollarSign size={15} className="mr-2 text-[#FFD600]" /> Pricing
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => nav("/contact")} data-testid="more-contact" className="cursor-pointer focus:bg-white/10">
              <Mail size={15} className="mr-2 text-[#00FFC2]" /> Contact
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav("/contact?type=bug")} data-testid="more-report-bug" className="cursor-pointer focus:bg-white/10">
              <Bug size={15} className="mr-2 text-[#FF0055]" /> Report a problem
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav("/dashboard")} data-testid="more-golive" className="cursor-pointer focus:bg-white/10">
              <Video size={15} className="mr-2 text-[#FF0055]" /> Go Live
            </DropdownMenuItem>

            <DropdownMenuSeparator className="bg-white/10" />
            <div className="px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Legal</div>
            <DropdownMenuItem onClick={() => nav("/terms")} data-testid="more-terms" className="cursor-pointer focus:bg-white/10">
              <FileText size={15} className="mr-2 text-zinc-400" /> Terms
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav("/privacy")} data-testid="more-privacy" className="cursor-pointer focus:bg-white/10">
              <Lock size={15} className="mr-2 text-zinc-400" /> Privacy
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav("/guidelines")} data-testid="more-guidelines" className="cursor-pointer focus:bg-white/10">
              <ShieldCheck size={15} className="mr-2 text-zinc-400" /> Community Guidelines
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav("/delete-account")} data-testid="more-delete" className="cursor-pointer text-[#FF0055] focus:bg-white/10">
              <Trash2 size={15} className="mr-2" /> Delete Account
            </DropdownMenuItem>

            {user && (
              <>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={() => nav("/profile")} data-testid="more-profile" className="cursor-pointer focus:bg-white/10">
                  <User size={15} className="mr-2 text-[#00FFC2]" /> Edit profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="more-logout" className="cursor-pointer text-[#FF0055] focus:bg-white/10">
                  <LogOut size={15} className="mr-2" /> Log out
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem onClick={() => window.dispatchEvent(new Event("open-walkthrough"))} data-testid="more-walkthrough" className="cursor-pointer focus:bg-white/10">
              <Sparkles size={15} className="mr-2 text-[#00FFC2]" /> Quick Start Guide
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => window.dispatchEvent(new Event("open-tour"))} data-testid="more-tour" className="cursor-pointer focus:bg-white/10">
              <PlayCircle size={15} className="mr-2 text-[#00FFC2]" /> Watch App Tour
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => window.dispatchEvent(new Event("open-rating"))} data-testid="more-rate" className="cursor-pointer focus:bg-white/10">
              <Star size={15} className="mr-2 text-[#FFD600]" /> Rate Air Wave
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <form onSubmit={submitSearch} className="ml-auto hidden max-w-xs flex-1 items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 focus-within:border-[#00FFC2] 2xl:flex" data-testid="search-form">
          <Search size={15} className="text-zinc-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="search-input"
            placeholder="Search channels, streams…"
            className="w-full bg-transparent text-sm text-white outline-none placeholder:text-zinc-500"
          />
        </form>

        <div className="ml-auto flex shrink-0 items-center gap-3 2xl:ml-0">
          <AccentPicker />
          {user ? (
            <>
              {!gated && (
                <button onClick={() => setShowCoins(true)} data-testid="navbar-coins"
                  className="hidden items-center gap-1.5 rounded-full bg-[#FFD600]/15 px-3 py-1.5 text-sm font-bold text-[#FFD600] transition-colors hover:bg-[#FFD600]/25 sm:flex">
                  <Coins size={15} /> {coins.toLocaleString()}
                </button>
              )}
              <NotificationBell />
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full border border-white/10 py-1 pl-1 pr-3 transition-colors hover:border-[#00FFC2]" data-testid="user-menu">
                  <span className="grid h-7 w-7 place-items-center rounded-full bg-[#00FFC2] font-display text-sm font-bold text-black">{user.name?.[0]}</span>
                  <span className="hidden max-w-[120px] truncate text-sm font-semibold sm:block">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border-white/10 bg-[#0C0C14] text-white">
                {!gated && (
                  <DropdownMenuItem onClick={() => nav("/premium")} data-testid="menu-premium" className="cursor-pointer focus:bg-white/10">
                    <Crown size={15} className="mr-2 text-[#FFD600]" /> Go Premium
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => nav("/subscriptions")} data-testid="menu-subs" className="cursor-pointer focus:bg-white/10">
                  <CreditCard size={15} className="mr-2 text-[#00FFC2]" /> Subscriptions
                </DropdownMenuItem>
                {(user.role === "creator" || user.role === "admin") && (
                  <DropdownMenuItem onClick={() => nav("/earnings")} data-testid="menu-earnings" className="cursor-pointer focus:bg-white/10">
                    <Wallet size={15} className="mr-2 text-[#00FFC2]" /> Earnings
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => nav("/parental")} data-testid="menu-parental" className="cursor-pointer focus:bg-white/10">
                  <ShieldCheck size={15} className="mr-2 text-[#FFD600]" /> Parental Controls
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/dashboard")} data-testid="menu-golive" className="cursor-pointer focus:bg-white/10">
                  <Video size={15} className="mr-2 text-[#FF0055]" /> Creator Studio
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <>
                  <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin" className="cursor-pointer focus:bg-white/10">
                    <Shield size={15} className="mr-2 text-[#00FFC2]" /> Admin Console
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/revenue")} data-testid="menu-revenue" className="cursor-pointer focus:bg-white/10">
                    <TrendingUp size={15} className="mr-2 text-[#00FFC2]" /> Revenue
                  </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem onClick={() => window.dispatchEvent(new Event("open-walkthrough"))} data-testid="menu-walkthrough" className="cursor-pointer focus:bg-white/10">
                  <Sparkles size={15} className="mr-2 text-[#00FFC2]" /> Quick Start Guide
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => window.dispatchEvent(new Event("open-tour"))} data-testid="menu-tour" className="cursor-pointer focus:bg-white/10">
                  <PlayCircle size={15} className="mr-2 text-[#00FFC2]" /> Watch Tour
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => window.dispatchEvent(new Event("open-rating"))} data-testid="menu-rate" className="cursor-pointer focus:bg-white/10">
                  <Star size={15} className="mr-2 text-[#FFD600]" /> Rate Air Wave
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="cursor-pointer text-[#FF0055] focus:bg-white/10">
                  <LogOut size={15} className="mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          ) : (
            <Button onClick={() => nav("/auth")} data-testid="login-cta" className="rounded-full bg-[#00FFC2] font-semibold text-black hover:bg-[#00E0A8]">
              Sign in
            </Button>
          )}
        </div>
      </div>

      {/* Full-width search on its own row on all laptops; inline only on large desktops (2xl+) */}
      <div className="shell pb-3 2xl:hidden">
        <form onSubmit={submitSearch} className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2.5 focus-within:border-[#00FFC2]" data-testid="search-form-mobile">
          <Search size={16} className="text-zinc-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="search-input-mobile"
            placeholder="Search channels, streams…"
            className="w-full bg-transparent text-sm text-white outline-none placeholder:text-zinc-500"
          />
        </form>
      </div>
      <BuyCoinsDialog open={showCoins} onOpenChange={setShowCoins} />
    </header>
  );
};
